import { $api } from '@shared/api';
import { camelizeKeys } from '@shared/helpers';

import { taskCommentsApiRoutes } from './api-routes';

import type {
    TCreateTaskCommentParams,
    TDeleteTaskCommentParams,
    TTaskCommentsData,
    TTaskCommentsDataDto,
    TTaskCommentsPollData,
    TTaskCommentsPollDataDto,
} from '../model/types';
import type { IDataResponse } from '@shared/model/types/dataResponse.inteface';

const parseTaskCommentsResponse = (
    response: IDataResponse<TTaskCommentsDataDto>
): TTaskCommentsData => {
    if (response.status !== 'success' || !response.data) {
        throw new Error(response.message || 'Failed to load task comments');
    }

    return camelizeKeys(response.data);
};

export const taskCommentsApi = {
    pollTaskComments: async (
        taskId: string,
        stateToken?: string
    ): Promise<TTaskCommentsPollData> => {
        const { data: response } = await $api.get<IDataResponse<TTaskCommentsPollDataDto>>(
            taskCommentsApiRoutes.pollTaskCommentsRoute(taskId),
            { params: { 'state_token': stateToken } }
        );

        if (response.status !== 'success' || !response.data) {
            throw new Error(response.message || 'Failed to poll task comments');
        }

        return camelizeKeys(response.data);
    },
    deleteTaskComment: async (
        { taskId, commentId }: TDeleteTaskCommentParams
    ): Promise<TTaskCommentsData> => {
        const { data: response } = await $api.delete<IDataResponse<TTaskCommentsDataDto>>(
            taskCommentsApiRoutes.deleteTaskCommentRoute(taskId, commentId)
        );

        return parseTaskCommentsResponse(response);
    },
    getTaskComments: async (taskId: string, signal?: AbortSignal): Promise<TTaskCommentsData> => {
        const { data: response } = await $api.get<IDataResponse<TTaskCommentsDataDto>>(
            taskCommentsApiRoutes.getTaskCommentsRoute(taskId),
            { signal }
        );

        return parseTaskCommentsResponse(response);
    },
    createTaskComment: async (
        { taskId, payload }: TCreateTaskCommentParams
    ): Promise<TTaskCommentsData> => {
        const text = payload.text.trim();
        if (!text && !payload.files.length) {
            throw new Error('Cannot send an empty task comment');
        }

        const formData = new FormData();
        if (text) {
            formData.append('text', text);
        }
        payload.files.forEach((file) => formData.append('files[]', file));

        const { data: response } = await $api.post<IDataResponse<TTaskCommentsDataDto>>(
            taskCommentsApiRoutes.createTaskCommentRoute(taskId),
            formData
        );

        return parseTaskCommentsResponse(response);
    },
};
